import { Collapse, CollapseProps, Form, FormInstance, Tabs, TabsProps } from 'antd';
import React, { memo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useRelayEnvironment } from 'react-relay';

import Activity from '../../components/Activity/Activity';
import Attachments from '../../components/Attachments/Attachments';
import WrapperTwoColumn from '../../components/WrapperTwoColumn';
import { getKeyCollapse, getObjectFromRelayStoreById } from '../../functions';
import useAccessForItem from '../../hooks/useAccessForItem';
import useObserverComponent from '../../hooks/useObserverComponent';
import { FieldPhase, FieldProject } from '../../index';
import { RootState } from '../../store';
import { ItemType } from '../../types';
import FieldContractInvoice from './fieldContractInvoice';
import FieldCostCode from './fieldCostCode';
import FieldDivision from './fieldDivision';
import FieldParent from './fieldParent';
import FieldWoSi from './fieldWoSi';

interface ITabsItems {
    form: FormInstance<ItemType>;
    projectId?: string;
    isEditable: boolean;
    isMainItem: boolean;
    updatedItemId?: string;
}

const TabsItems: React.FC<ITabsItems> = ({ form, updatedItemId, isEditable, projectId, isMainItem }) => {
    const environment = useRelayEnvironment();
    const isDragging = useSelector<RootState, boolean>((state) => state.gantDraggable.isDragging);
    const refListProject = useRef<React.FC<any>>(null);

    const { isVisible: isVisibleAttachments, ref: refAttachments } = useObserverComponent();
    const { isVisible: isVisibleActivity, ref: refActivity } = useObserverComponent();

    const styleCollapseItems = { marginTop: -12, marginBottom: -12 };

    // const projectFilter = { project_id: projectId || '', role_item: ['phase', 'workItem'] };
    const filterFlexible = projectId
        ? `[{"or":[{"project_id":"${projectId}"}]},{"or":[{"role_item":"phase|workItem"}]}]`
        : undefined;

    const currentItem = getObjectFromRelayStoreById(environment, updatedItemId!) as ItemType;

    const isAccessActivity = {
        create: useAccessForItem(currentItem, ['work-item.view']),
    };

    const isAccessAttachments = {
        view: useAccessForItem(currentItem, ['work-item.all.view', 'work-item.owner.view', 'work-item.assignee.view']),
        delete: useAccessForItem(currentItem, [
            'work-item.all.manage',
            'work-item.owner.manage',
            'work-item.assignee.manage',
        ]),
    };

    const collapseWorkItems: CollapseProps['items'] = [
        {
            key: '1',
            label: 'Dependencies',
            children: (
                <>
                    <WrapperTwoColumn
                        childFirstColumn={
                            <>
                                <FieldProject<ItemType>
                                    name={'projectId'}
                                    disabled={true}
                                    ref={refListProject as any}
                                />
                                {/* disabled={isMainItem || !isEditable} */}
                                <FieldParent disabled={true} filterFlexible={filterFlexible} />
                            </>
                        }
                        childSecondColumn={
                            <>
                                <FieldPhase<ItemType>
                                    parentRef={refListProject as any}
                                    name={'phaseId'}
                                    disabled={true}
                                />
                                {/*<FieldBondRight filterFlexible={filterFlexible} disabled={!isEditable} />*/}
                            </>
                        }
                        style={styleCollapseItems}
                    />
                    {/*<div style={{ marginTop: 12 }}>*/}
                    {/*    <FieldChildren disabled={isMainItem || !isEditable} filterFlexible={filterFlexible} />*/}
                    {/*</div>*/}
                </>
            ),
            forceRender: true,
        },
        {
            key: '2',
            label: 'Context',
            children: (
                <WrapperTwoColumn
                    childFirstColumn={
                        <>
                            <FieldDivision disabled={!isEditable} />
                            <FieldContractInvoice disabled={!isEditable} />
                        </>
                    }
                    childSecondColumn={
                        <>
                            <FieldCostCode disabled={!isEditable} />
                            <FieldWoSi disabled={!isEditable} />
                        </>
                    }
                    style={styleCollapseItems}
                />
            ),
            forceRender: true,
        },
    ];

    const tabsItems: TabsProps['items'] = [
        {
            key: '1',
            label: 'Work Item',
            children: (
                <Form name="scheduleItemFrom3" layout="vertical" form={form} autoComplete="off">
                    <Collapse defaultActiveKey={getKeyCollapse(collapseWorkItems)} ghost items={collapseWorkItems} />
                </Form>
            ),
            forceRender: true,
        },
        {
            key: '2',
            label: 'Activities',
            children: updatedItemId ? (
                <div ref={refActivity} style={{ margin: '12px 0' }}>
                    {isVisibleActivity && (
                        <Activity
                            model_id={updatedItemId}
                            model_type={'ScheduleItem'}
                            isDragging={isDragging}
                            isAccess={isAccessActivity}
                        />
                    )}
                </div>
            ) : null,
            forceRender: true,
        },
        {
            key: '3',
            label: 'Attachments',
            children: updatedItemId ? (
                <div ref={refAttachments} style={{ margin: '12px 0' }}>
                    {isVisibleAttachments && !isDragging && (
                        <Attachments
                            model_id={updatedItemId}
                            model_type={'ScheduleItem'}
                            isAccess={isAccessAttachments}
                        />
                    )}
                </div>
            ) : null,
            forceRender: true,
        },
    ];

    return (
        <Tabs defaultActiveKey="1" items={tabsItems} centered type="card" size={'small'} style={{ marginTop: 10 }} />
    );
};

export default memo(TabsItems);
